import React from 'react';
import {
    Box,
    Typography,
    Chip,
    Paper,
    IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CampaignIcon from '@mui/icons-material/Campaign';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { LABELS, STYLE_CONSTANTS } from './SupportDetailsTab.constants';
import {
    getSupportStatusConfig,
    formatCommentDate
} from './SupportDetailsTab.utils';

export const DepartmentSupportSection = ({
                                             departmentType,
                                             departmentLabel,
                                             supportProvided,
                                             selectedMember,
                                             comment,
                                             onExpandComment,
                                             theme
                                         }) => {
    const statusConfig = getSupportStatusConfig(supportProvided);
    const DepartmentIcon = departmentType === 'MARKETING' ? CampaignIcon : LocalShippingIcon;
    const StatusIcon = supportProvided ? DoneAllIcon : HourglassEmptyIcon;

    return (
        <Box>
            <Box sx={styles.headerContainer}>
                <DepartmentIcon sx={styles.departmentIcon(theme)} />
                <Typography variant="subtitle2" sx={styles.departmentTitle}>
                    {departmentLabel}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Chip
                    size="small"
                    label={statusConfig.label}
                    color={statusConfig.color}
                    icon={<StatusIcon />}
                    sx={styles.statusChip}
                />
            </Box>

            {selectedMember ? (
                <Paper variant="outlined" sx={styles.memberPaper(theme)}>
                    <Box sx={styles.memberInfo}>
                        <PersonIcon sx={styles.personIcon(theme)} />
                        <Typography variant="body2" sx={styles.memberName}>
                            {selectedMember.name || selectedMember.username}
                        </Typography>
                        {selectedMember.email && (
                            <Typography variant="body2" sx={styles.memberEmail(theme)}>
                                {selectedMember.email}
                            </Typography>
                        )}
                    </Box>

                    {!supportProvided ? (
                        <Typography variant="body2" color="text.secondary" sx={styles.noCommentText}>
                            {LABELS.NO_COMMENT_YET}
                        </Typography>
                    ) : comment && (
                        <Box sx={styles.commentSection}>
                            <Typography variant="body2" sx={styles.commentLabel}>
                                {LABELS.COMMENT}
                            </Typography>
                            <Box sx={{ position: 'relative' }}>
                                // ... continuing from previous
                                <IconButton
                                    size="small"
                                    sx={styles.expandButton(theme)}
                                    onClick={onExpandComment}
                                    aria-label={LABELS.EXPAND_COMMENT}
                                >
                                    <OpenInFullIcon fontSize="small" />
                                </IconButton>

                                <Box sx={styles.commentContent(theme)}>
                                    {comment.comment}
                                </Box>
                            </Box>

                            <Box sx={styles.approvalContainer}>
                                <Chip
                                    size="small"
                                    label={comment.isApproved ? LABELS.APPROVED : LABELS.NOT_APPROVED}
                                    color={comment.isApproved ? 'success' : 'error'}
                                    icon={comment.isApproved ? <CheckCircleIcon /> : <WarningIcon />}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {formatCommentDate(comment.createdAt)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={styles.noMemberText}>
                    {LABELS.NO_MEMBER_SELECTED}
                </Typography>
            )}
        </Box>
    );
};

const styles = {
    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        mb: 1
    },
    departmentIcon: (theme) => ({
        color: theme.palette.primary.main,
        mr: 1
    }),
    departmentTitle: {
        fontSize: STYLE_CONSTANTS.FONT_SIZES.SUBTITLE
    },
    statusChip: {
        ml: 1
    },
    memberPaper: (theme) => ({
        p: 1.5,
        bgcolor: theme.palette.background.paper
    }),
    memberInfo: {
        display: 'flex',
        alignItems: 'center',
        mb: 0.5
    },
    personIcon: (theme) => ({
        fontSize: STYLE_CONSTANTS.PERSON_ICON_SIZE,
        mr: 0.5,
        color: theme.palette.text.secondary
    }),
    memberName: {
        fontSize: STYLE_CONSTANTS.FONT_SIZES.BODY,
        fontWeight: 500
    },
    memberEmail: (theme) => ({
        fontSize: STYLE_CONSTANTS.FONT_SIZES.SMALL,
        ml: 1,
        color: theme.palette.text.secondary
    }),
    noCommentText: {
        fontSize: STYLE_CONSTANTS.FONT_SIZES.SMALL,
        fontStyle: 'italic',
        mt: 1
    },
    commentSection: {
        mt: 1,
        position: 'relative'
    },
    commentLabel: {
        fontWeight: 500,
        fontSize: STYLE_CONSTANTS.FONT_SIZES.BODY
    },
    expandButton: (theme) => ({
        position: 'absolute',
        top: 4,
        right: 4,
        zIndex: 1,
        scale: STYLE_CONSTANTS.ICON_SCALE,
        bgcolor: 'rgba(255,255,255,0.7)',
        '&:hover': {
            bgcolor: 'rgba(255,255,255,0.9)',
            color: theme.palette.primary.main
        }
    }),
    commentContent: (theme) => ({
        fontSize: STYLE_CONSTANTS.FONT_SIZES.BODY,
        mb: 0.5,
        maxHeight: STYLE_CONSTANTS.MAX_COMMENT_HEIGHT,
        overflowY: 'auto',
        bgcolor: theme.palette.action.hover,
        borderRadius: 1,
        p: 1,
        pr: 5,
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
    }),
    approvalContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 1
    },
    noMemberText: {
        fontSize: STYLE_CONSTANTS.FONT_SIZES.SMALL,
        fontStyle: 'italic'
    }
};